/**
 * When should useResults keep polling the match_results cache?
 *
 *   'fetch' - some match has kicked off (within POLL_STALE_MS) and has no final
 *             status yet: poll now.
 *   'wait'  - nothing to fetch now, but a match is still to kick off: keep the
 *             timer alive so a page opened before kickoff picks up scores.
 *   'stop'  - everything relevant is finished / postponed / cancelled (or long
 *             stale): stop polling.
 */

const TERMINAL = new Set(['FINISHED', 'POSTPONED', 'CANCELLED']);

// A match that kicked off this long ago with still no final status is not going
// to be resolved by polling every 2 minutes (e.g. a team-name mismatch).
export const POLL_STALE_MS = 6 * 60 * 60 * 1000;

/**
 * Parse kickoff time string to timestamp (ms)
 * Handles format like "Jan 20 2026 - 8:00pm" (GMT)
 */
export function parseKickoffTime(dateStr) {
  if (!dateStr || dateStr === 'TBD') return null;

  try {
    if (dateStr.includes(' - ')) {
      const [datePart, timePart] = dateStr.split(' - ');
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(am|pm)/i);

      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const period = timeMatch[3].toLowerCase();

        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;

        // Pad minutes to 2 digits — Safari's Date parser rejects "14:5:00 GMT".
        const paddedMinutes = String(minutes).padStart(2, '0');
        const date = new Date(`${datePart} ${hours}:${paddedMinutes}:00 GMT`);
        if (!isNaN(date.getTime())) {
          return date.getTime();
        }
      }
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date.getTime();
  } catch {
    return null;
  }
}

export function resultsPollAction(matches, results, now) {
  let upcoming = false;
  for (const m of matches || []) {
    // A prediction can be marked POSTPONED/CANCELLED by the pipeline itself
    // (m.status) before the results feed ever carries that status — skip it
    // either way.
    if (TERMINAL.has(m.status)) continue;
    const api = results?.[m.id]?.status;
    const status = api && api !== 'SCHEDULED' ? api : (m.result?.status || api);
    if (TERMINAL.has(status)) continue;
    const kickoff = parseKickoffTime(m.date_time);
    if (kickoff == null) continue;
    if (kickoff > now) {
      upcoming = true;
    } else if (now - kickoff <= POLL_STALE_MS) {
      return 'fetch';
    }
  }
  return upcoming ? 'wait' : 'stop';
}
